import React, { useState, useEffect } from 'react'
import { 
  Search, Filter, Download, RefreshCw, Eye, 
  ArrowUpRight, ArrowDownRight, DollarSign,
  Calendar, User, TrendingUp, Activity
} from 'lucide-react'
import { supabase } from '../../services/supabase'
import { formatCurrency, formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'

const AllTransactions = () => {
  const [transactions, setTransactions] = useState([])
  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalVolume: 0
  })

  useEffect(() => {
    loadTransactions()
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('transactions_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'transactions' },
        (payload) => {
          console.log('Transaction change:', payload)
          loadTransactions() // Reload on any change
          toast.success('New transaction detected!')
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    filterTransactions()
    calculateStats()
  }, [transactions, searchTerm, typeFilter, statusFilter, dateFilter])

const loadTransactions = async () => {
  setLoading(true)
  try {
    // First get transactions
    const { data: transactionsData, error: txError } = await supabase
      .from('transactions')
      .select('*')
      .order('created_at', { ascending: false })

    if (txError) throw txError

    // Then get user info for each transaction
    if (transactionsData && transactionsData.length > 0) {
      const userIds = [...new Set(transactionsData.map(t => t.user_id).filter(Boolean))]
      
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, username, email, full_name, name')
        .in('id', userIds)

      if (usersError) throw usersError

      // Merge user data with transactions
      const transactionsWithUsers = transactionsData.map(tx => ({
        ...tx,
        users: usersData?.find(u => u.id === tx.user_id) || null
      }))

      setTransactions(transactionsWithUsers)
    } else {
      setTransactions([])
    }
  } catch (error) {
    console.error('Error loading transactions:', error)
    toast.error('Failed to load transactions')
  } finally {
    setLoading(false)
  }
}

  const filterTransactions = () => {
    let filtered = [...transactions]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.users?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.users?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.users?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.type === typeFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter)
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date()
      const filterDate = new Date()
      
      switch(dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0)
          break
        case 'week':
          filterDate.setDate(now.getDate() - 7)
          break
        case 'month':
          filterDate.setMonth(now.getMonth() - 1)
          break
      }
      
      filtered = filtered.filter(t => new Date(t.created_at) >= filterDate)
    }

    setFilteredTransactions(filtered)
  }

  const calculateStats = () => {
    const total = transactions.length
    const pending = transactions.filter(t => t.status === 'pending').length
    const completed = transactions.filter(t => t.status === 'completed').length
    const totalVolume = transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)

    setStats({ total, pending, completed, totalVolume })
  }

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction)
    setShowDetailsModal(true)
  }

  const handleExport = () => {
    const csv = [
      ['Date', 'User', 'Type', 'Amount', 'Status', 'Description'],
      ...filteredTransactions.map(t => [
        formatDate(t.created_at),
        t.users?.email || 'Unknown',
        t.type,
        t.amount,
        t.status,
        t.description || ''
      ])
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    toast.success('Transactions exported!')
  }

  const getTypeBadge = (type) => {
    const badges = {
      deposit: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      withdrawal: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      transfer: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      payment: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
    }
    return badges[type] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
  }

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      cancelled: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
    }
    return badges[status] || badges.pending
  }

  const getTypeIcon = (type) => {
    if (type === 'deposit') return <ArrowDownRight className="w-4 h-4 text-green-600" />
    if (type === 'withdrawal') return <ArrowUpRight className="w-4 h-4 text-red-600" />
    return <DollarSign className="w-4 h-4 text-blue-600" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            All Transactions
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {filteredTransactions.length} of {transactions.length} transactions
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={loadTransactions}
            className="px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Total</span>
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {stats.total}
          </p>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Pending</span>
            <Activity className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {stats.pending}
          </p>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Completed</span>
            <Activity className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {stats.completed}
          </p>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Volume</span>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(stats.totalVolume)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
            />
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
            <option value="transfer">Transfer</option>
            <option value="payment">Payment</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-dark-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No transactions found
                  </td>
                </tr>
              ) : (
                filteredTransactions.map(transaction => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {transaction.users?.full_name || transaction.users?.name || transaction.users?.username}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {transaction.users?.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center gap-1 ${getTypeBadge(transaction.type)}`}>
                        {getTypeIcon(transaction.type)}
                        {transaction.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => handleViewDetails(transaction)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Transaction Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Transaction ID */}
              <div className="pb-4 border-b border-gray-200 dark:border-dark-700">
                <label className="text-xs text-gray-500 dark:text-gray-400">Transaction ID</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white break-all mt-1">
                  {selectedTransaction.id}
                </p>
              </div>

              {/* Main Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Type</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeBadge(selectedTransaction.type)}`}>
                      {selectedTransaction.type}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(selectedTransaction.status)}`}>
                      {selectedTransaction.status}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Amount</label>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Date</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                    {formatDate(selectedTransaction.created_at)}
                  </p>
                </div>
              </div>

              {/* User Info */}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">User</label>
                <div className="flex items-center gap-3 mt-2 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedTransaction.users?.full_name || selectedTransaction.users?.name || selectedTransaction.users?.username}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedTransaction.users?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Balance Changes */}
              {(selectedTransaction.balance_before !== null || selectedTransaction.balance_after !== null) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Balance Before</label>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                      {formatCurrency(selectedTransaction.balance_before)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Balance After</label>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                      {formatCurrency(selectedTransaction.balance_after)}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedTransaction.description && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Description</label>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {selectedTransaction.description}
                  </p>
                </div>
              )}

              {/* Metadata */}
              {selectedTransaction.metadata && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Additional Info</label>
                  <pre className="text-xs text-gray-900 dark:text-white mt-1 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg overflow-auto">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-dark-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllTransactions